/**
 * Programmatic re-load inspection of an exported GLB.
 *
 * SPEC: "Export success is not established merely because a Blob was created." Every export runs
 * back through here, and tests assert on the result. Two independent views are combined:
 *  - the `GLTFLoader` view (what a consumer actually gets: clips, tracks, skinned meshes), and
 *  - the raw JSON chunk (what the exporter wrote), because `GLTFLoader.loadSkin` silently skips
 *    joints that point at missing nodes, hiding a broken `skins[].joints` array.
 */

import { Bone, PropertyBinding, SkinnedMesh } from 'three';
import type { Mesh } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

import { AppError } from '../shared';

export type GlbTrackType = 'quaternion' | 'position' | 'scale' | 'other';

export interface GlbTrackInfo {
  name: string;
  type: GlbTrackType;
  /** Number of samples on the track. */
  times: number;
}

export interface GlbClipInfo {
  name: string;
  duration: number;
  tracks: GlbTrackInfo[];
}

export interface GlbInspection {
  /** Joint names in `skins[].joints` order (deduped); falls back to scene bones when unskinned. */
  boneNames: string[];
  skinnedMeshCount: number;
  clips: GlbClipInfo[];
  /**
   * Position tracks that target the mapped Hips node — the vertical hips offset channel, the one
   * position track this project's exporter is allowed to write. At most one per clip, summed over
   * all clips in the file. Always 0 when nothing identifies which node the exception applies to:
   * no `hipsNodeName` from the caller AND no Hips bone in the file's own `animstatex` metadata.
   */
  hipsPositionTrackCount: number;
  /**
   * The export gate. True when the file carries any SCALE track, or any POSITION track on a node
   * OTHER than the allowed Hips one.
   *
   * It used to mean "any position or scale track at all" — the rotation-only invariant was
   * absolute. It is no longer: a crouch cannot be expressed as a rotation, so a clip that authors
   * `hipsY` bakes exactly one `<Hips>.position` track. That single track is counted in
   * `hipsPositionTrackCount` and is NOT a reason to refuse an export; a `LeftFoot.position`, a
   * root-motion track or any scale track still is.
   */
  hasPositionOrScaleTracks: boolean;
  /** `userData.animstatex` found on the scene or any node. */
  metadata: unknown;
  /**
   * How many images the file embeds, straight from the raw JSON. The texture-free export's whole
   * claim is that this is `0`, and "the loader gave me a material with a null `map`" does not
   * prove it — an image can sit in the file unreferenced. `0` for a file with no `images` array,
   * and `0` when the JSON chunk could not be read (the `warnings` say so).
   */
  imageCount: number;
  /**
   * Meshes in the re-loaded scene whose geometry still carries a `uv` attribute.
   *
   * This is the OTHER half of the texture-free claim and the one the user actually asked about:
   * dropping the textures must not drop the UV map, or the textures can never be put back. The
   * export compares this against what the live model had before it was written.
   */
  uvMeshCount: number;
  /** Total meshes in the re-loaded scene, so `uvMeshCount` can be read as a ratio. */
  meshCount: number;
  /** Material names in traversal order, deduped — what `applyTextures` will have to match. */
  materialNames: string[];
  warnings: string[];
}

export interface GlbInspectOptions {
  /**
   * The source bone name of the canonical Hips, when the caller already knows it. Takes precedence
   * over the file's `animstatex` metadata; the metadata stays the fallback, so a buffer-only call
   * behaves exactly as before.
   *
   * This exists because the exception the `hipsPositionTrackCount` / `hasPositionOrScaleTracks`
   * pair grants is otherwise conditional on metadata surviving the round trip. `exportGlb`
   * validates the buffer it JUST wrote against a rig it is HOLDING — making it depend on reading
   * its own metadata back means any future export path that drops or reshapes that block would
   * make the validator refuse our own legal Hips track as "the rotation-only invariant is broken".
   * Safe direction, but self-inflicted, and confusing to whoever hits it.
   */
  hipsNodeName?: string;
}

const GLB_MAGIC = 0x46546c67; // 'glTF', little endian
const GLB_CHUNK_JSON = 0x4e4f534a; // 'JSON'
const GLB_CHUNK_BIN = 0x004e4942; // 'BIN\0'
const GLB_HEADER_BYTES = 12;

interface RawGltfJson {
  nodes?: Array<{ name?: unknown } | null>;
  skins?: Array<{ joints?: unknown; name?: unknown } | null>;
  animations?: unknown[];
  images?: unknown[];
}

/**
 * Walk a GLB container's chunks and return the first one of `wanted`, or `null` when the buffer is
 * not a GLB or holds no such chunk. Chunk order is not guaranteed by the spec, so this scans
 * rather than assuming JSON-then-BIN.
 */
function readGlbChunk(buffer: ArrayBuffer, wanted: number): Uint8Array | null {
  if (buffer.byteLength < GLB_HEADER_BYTES + 8) return null;
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== GLB_MAGIC) return null;

  let offset = GLB_HEADER_BYTES;
  while (offset + 8 <= buffer.byteLength) {
    const chunkLength = view.getUint32(offset, true);
    const chunkType = view.getUint32(offset + 4, true);
    offset += 8;
    if (offset + chunkLength > buffer.byteLength) return null;
    if (chunkType === wanted) return new Uint8Array(buffer, offset, chunkLength);
    offset += chunkLength;
  }
  return null;
}

/**
 * Read the JSON chunk out of a GLB container without going through `GLTFLoader`.
 * Returns `null` when the buffer is not a GLB or the chunk cannot be parsed.
 */
export function readGlbJsonChunk(buffer: ArrayBuffer): Record<string, unknown> | null {
  const chunk = readGlbChunk(buffer, GLB_CHUNK_JSON);
  if (!chunk) return null;
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(chunk));
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Read the BIN chunk — geometry, animation samples and embedded image bytes — as a view over the
 * original buffer. `null` for a GLB that has none (legal: a glTF whose buffers are all external).
 *
 * A VIEW, not a copy: the whole point of reading it is to slice a few image ranges out of a file
 * that may be tens of megabytes, and the callers do their own `slice` for the parts they keep.
 */
export function readGlbBinaryChunk(buffer: ArrayBuffer): Uint8Array | null {
  return readGlbChunk(buffer, GLB_CHUNK_BIN);
}

function trackType(name: string): GlbTrackType {
  if (name.endsWith('.quaternion')) return 'quaternion';
  if (name.endsWith('.position')) return 'position';
  if (name.endsWith('.scale')) return 'scale';
  return 'other';
}

const POSITION_SUFFIX = '.position';

/**
 * Normalize a candidate Hips node name for comparison against a track name.
 *
 * Both sides go through `sanitizeNodeName`: `GLTFLoader` sanitizes node names on reload and builds
 * its track names from the sanitized ones, while a caller (or the metadata) carries whatever name
 * the exporter was handed (vault `glb-export-bone-names`). For a file this project exported the
 * two are already identical — the exporter refuses an unsafe bone name — but a hand-assembled
 * file, or a caller reading a name straight off a live skeleton, need not be.
 */
function normalizeNodeName(name: unknown): string | null {
  if (typeof name !== 'string' || name.length === 0) return null;
  return PropertyBinding.sanitizeNodeName(name);
}

/**
 * The node name a position track is allowed to target, read out of the file's OWN `animstatex`
 * metadata (`canonicalBoneMap.Hips`). `null` when the file carries no metadata or no Hips mapping
 * — then nothing in the FILE identifies the exception, and unless the caller named the node
 * itself every position track counts as "some other node's", which is the safe answer for a gate
 * that refuses exports.
 */
function allowedHipsNodeName(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const map = (metadata as { canonicalBoneMap?: unknown }).canonicalBoneMap;
  if (!map || typeof map !== 'object') return null;
  return normalizeNodeName((map as Record<string, unknown>).Hips);
}

function findMetadata(gltf: GLTF): unknown {
  const scene = gltf.scene ?? gltf.scenes?.[0];
  if (!scene) return undefined;
  if (scene.userData && scene.userData.animstatex !== undefined) return scene.userData.animstatex;

  let found: unknown;
  scene.traverse((o) => {
    if (found === undefined && o.userData && o.userData.animstatex !== undefined) {
      found = o.userData.animstatex;
    }
  });
  return found;
}

/** Joint names straight from the raw JSON, plus warnings for joints that point nowhere. */
function jointNamesFromJson(
  json: RawGltfJson | null,
  warnings: string[],
): string[] | null {
  if (!json || !Array.isArray(json.skins) || json.skins.length === 0) return null;

  const nodes = Array.isArray(json.nodes) ? json.nodes : [];
  const names: string[] = [];
  const seen = new Set<string>();

  json.skins.forEach((skin, skinIndex) => {
    const joints = skin && Array.isArray(skin.joints) ? skin.joints : null;
    if (!joints) {
      warnings.push(`skins[${skinIndex}] has no joints array.`);
      return;
    }
    joints.forEach((joint, jointIndex) => {
      if (typeof joint !== 'number' || !Number.isInteger(joint) || joint < 0) {
        warnings.push(
          `skins[${skinIndex}].joints[${jointIndex}] is undefined — a skeleton bone was not ` +
            'part of the exported node graph.',
        );
        return;
      }
      const node = nodes[joint];
      if (!node) {
        warnings.push(
          `skins[${skinIndex}].joints[${jointIndex}] points at missing node ${joint}.`,
        );
        return;
      }
      const name = typeof node.name === 'string' ? node.name : `node_${joint}`;
      if (!seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    });
  });

  return names;
}

function parseGltf(buffer: ArrayBuffer): Promise<GLTF> {
  return new Promise<GLTF>((resolve, reject) => {
    const loader = new GLTFLoader();
    try {
      loader.parse(
        buffer,
        '',
        (gltf) => resolve(gltf),
        (event: unknown) => reject(event),
      );
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Load a GLB back and report what a consumer would actually see.
 *
 * `options` is entirely optional and additive — a buffer-only call answers exactly what it always
 * did. Pass `hipsNodeName` when you already know which node the vertical hips offset belongs to
 * (`exportGlb` does: it holds the rig) instead of making that depend on the file's own metadata.
 *
 * @throws AppError `GLB_INSPECT_FAILED` when the buffer cannot be re-loaded.
 */
export async function inspectGlb(
  buffer: ArrayBuffer,
  options: GlbInspectOptions = {},
): Promise<GlbInspection> {
  const warnings: string[] = [];
  const json = readGlbJsonChunk(buffer) as RawGltfJson | null;

  let gltf: GLTF;
  try {
    gltf = await parseGltf(buffer);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : String((error as { message?: unknown })?.message ?? error);
    throw new AppError(
      'GLB_INSPECT_FAILED',
      `The exported GLB could not be loaded back: ${message}`,
      error,
    );
  }

  const scene = gltf.scene ?? gltf.scenes?.[0];
  if (!scene) {
    throw new AppError('GLB_INSPECT_FAILED', 'The exported GLB contains no scene.');
  }

  const sceneBoneNames: string[] = [];
  const materialNames: string[] = [];
  let skinnedMeshCount = 0;
  let meshCount = 0;
  let uvMeshCount = 0;
  scene.traverse((o) => {
    if (o instanceof SkinnedMesh) skinnedMeshCount++;
    if (o instanceof Bone && !sceneBoneNames.includes(o.name)) sceneBoneNames.push(o.name);
    const mesh = o as Mesh;
    if (mesh.isMesh !== true) return;
    meshCount++;
    if (mesh.geometry?.getAttribute('uv')) uvMeshCount++;
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (material && !materialNames.includes(material.name)) materialNames.push(material.name);
    }
  });

  const jsonJointNames = jointNamesFromJson(json, warnings);
  const boneNames = jsonJointNames && jsonJointNames.length > 0 ? jsonJointNames : sceneBoneNames;

  if (json === null) {
    warnings.push('The GLB JSON chunk could not be read; skin joints were not cross-checked.');
  }

  const metadata = findMetadata(gltf);
  // Caller first, file second: whoever holds the rig knows better than a block of JSON that has
  // been through an exporter.
  const hipsNodeName = normalizeNodeName(options.hipsNodeName) ?? allowedHipsNodeName(metadata);

  let hasPositionOrScaleTracks = false;
  let hipsPositionTrackCount = 0;
  const clips: GlbClipInfo[] = (gltf.animations ?? []).map((clip) => ({
    name: clip.name,
    duration: clip.duration,
    tracks: clip.tracks.map((track) => {
      const type = trackType(track.name);
      if (type === 'scale') {
        hasPositionOrScaleTracks = true;
      } else if (type === 'position') {
        const node = PropertyBinding.sanitizeNodeName(
          track.name.slice(0, track.name.length - POSITION_SUFFIX.length),
        );
        if (hipsNodeName !== null && node === hipsNodeName) hipsPositionTrackCount++;
        else hasPositionOrScaleTracks = true;
      }
      return { name: track.name, type, times: track.times.length };
    }),
  }));

  return {
    boneNames,
    skinnedMeshCount,
    clips,
    hipsPositionTrackCount,
    hasPositionOrScaleTracks,
    metadata,
    imageCount: Array.isArray(json?.images) ? json.images.length : 0,
    uvMeshCount,
    meshCount,
    materialNames,
    warnings,
  };
}
